import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { hasMerchantPermission } from "../../api/merchantAuthApi";
import { getPickupQueue, markPickupOrderCollected } from "../../api/pickupQueueApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./pickup-queue.scss";

const emptyListing = {
  product_name: "No products waiting",
  original_price: "—",
  price: "—",
  pickup_window: "—",
};

const parseTimeToMinutes = (timeValue) => {
  const match = String(timeValue || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3].toUpperCase();
  if (hours === 12) hours = 0;
  if (period === "PM") hours += 12;
  return (hours * 60) + minutes;
};

const isPickupWindowOpen = (pickupWindow, currentDate = new Date()) => {
  const [startValue, endValue] = String(pickupWindow || "").split("-").map((value) => value.trim());
  const startMinutes = parseTimeToMinutes(startValue);
  const endMinutes = parseTimeToMinutes(endValue);
  if (startMinutes === null || endMinutes === null) return true;

  const currentMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();
  if (endMinutes < startMinutes) {
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
};

const publishWaitingCount = (count) => {
  localStorage.setItem("pickup_waiting_count", String(count));
  window.dispatchEvent(new CustomEvent("pickup-waiting-count-updated", {
    detail: { count },
  }));
};

const PickupQueuePage = () => {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutletContext();
  const [keyword, setKeyword] = useState("");
  const [queue, setQueue] = useState({
    waiting_count: 0,
    collected_count: 0,
    listing: emptyListing,
    orders: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const canCollect = hasMerchantPermission("orders_collect");

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedOutlet?.id) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    setSuccess("");

    getPickupQueue(selectedOutlet.id)
      .then((data) => {
        if (!active) return;
        const nextQueue = {
          waiting_count: Number(data?.waiting_count || 0),
          collected_count: Number(data?.collected_count || 0),
          listing: data?.listing || emptyListing,
          orders: Array.isArray(data?.orders) ? data.orders : [],
        };
        setQueue(nextQueue);
        publishWaitingCount(nextQueue.waiting_count);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedOutlet?.id]);

  const filteredOrders = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    if (!search) return queue.orders;

    return queue.orders.filter((order) =>
      `${order.order_number} ${order.customer_name} ${order.customer_phone}`
        .toLowerCase()
        .includes(search)
    );
  }, [keyword, queue.orders]);

  const handleConfirmCollected = async () => {
    if (!confirmOrder || !selectedOutlet?.id || submitting) return;

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      await markPickupOrderCollected(selectedOutlet.id, confirmOrder.id);
      const nextOrders = queue.orders.filter((order) => order.id !== confirmOrder.id);
      const nextWaitingCount = Math.max(0, queue.waiting_count - 1);
      setQueue((current) => ({
        ...current,
        orders: nextOrders,
        waiting_count: nextWaitingCount,
        collected_count: current.collected_count + 1,
      }));
      publishWaitingCount(nextWaitingCount);
      setSuccess(`${confirmOrder.order_number} has been marked as collected.`);
      setConfirmOrder(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const listing = queue.listing || emptyListing;

  return (
    <ModulePageShell className="pickup-queue-page">
      <ModuleHeader
        bleed
        title="Pickup Queue"
        description="Focus on today's paid orders that are ready for customer collection."
        actions={
          <ModuleHeaderAction onClick={() => navigate("/orders")}>
            View All Orders
          </ModuleHeaderAction>
        }
      />

      {error && <div className="pickup-queue-message pickup-queue-message--error">{error}</div>}
      {success && <div className="pickup-queue-message pickup-queue-message--success">{success}</div>}

      <section className="pickup-queue-tools">
        <div className="pickup-queue-tools__metric">
          <span>Waiting Pickup</span>
          <strong>{queue.waiting_count}</strong>
        </div>
        <div className="pickup-queue-tools__metric">
          <span>Collected</span>
          <strong>{queue.collected_count}</strong>
        </div>
        <label className="pickup-queue-tools__search">
          <span>Search</span>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="Search order no, customer, or phone..."
          />
        </label>
      </section>

      <section className="pickup-queue-card">
        <div className="pickup-queue-card__header">
          <div>
            <h3>Today&apos;s Pickup Orders</h3>
            <p>Live ready-for-pickup orders for {selectedOutlet?.name || "the selected outlet"}.</p>
          </div>
        </div>

        <div className="pickup-listing-summary">
          <div>
            <span>Product Name</span>
            <strong>{listing.product_name}</strong>
          </div>
          <div>
            <span>Value</span>
            <strong>{listing.original_price}</strong>
            <small>Original price</small>
          </div>
          <div>
            <span>Price</span>
            <strong>{listing.price}</strong>
            <small>Discounted price</small>
          </div>
          <div>
            <span>Pickup Window</span>
            <strong>{listing.pickup_window}</strong>
          </div>
        </div>

        {loading ? (
          <div className="pickup-queue-empty">
            <h3>Loading pickup queue...</h3>
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="pickup-queue-list">
            {filteredOrders.map((order) => {
              const windowOpen = isPickupWindowOpen(order.pickup_window, currentTime);
              const actionEnabled = canCollect && windowOpen;
              const actionTitle = !canCollect
                ? "Your role does not allow marking orders as collected"
                : (windowOpen
                    ? "Mark this order as collected"
                    : `Available during ${order.pickup_window}`);

              return (
                <article className="pickup-order-card" key={order.id}>
                  <div className="pickup-order-card__main">
                    <div>
                      <span className="pickup-order-card__label">Order No</span>
                      <button
                        className="pickup-order-card__link"
                        type="button"
                        onClick={() => navigate(`/orders/${order.order_number}`)}
                      >
                        {order.order_number}
                      </button>
                    </div>
                    <div>
                      <span className="pickup-order-card__label">Customer</span>
                      <strong>{order.customer_name} / {order.customer_phone}</strong>
                      <small>{order.product_name}</small>
                    </div>
                    <div>
                      <span className="pickup-order-card__label">Qty</span>
                      <strong>{order.quantity}</strong>
                    </div>
                    <div>
                      <span className="pickup-order-card__label">Status</span>
                      <strong>{order.status}</strong>
                      <small>{order.pickup_window}</small>
                    </div>
                    <div className="pickup-order-card__action">
                      <button
                        type="button"
                        disabled={!actionEnabled}
                        title={actionTitle}
                        onClick={() => setConfirmOrder(order)}
                      >
                        Mark Collected
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="pickup-queue-empty">
            <h3>No pickup orders found</h3>
            <p>Ready for pickup orders will appear here automatically.</p>
          </div>
        )}
      </section>

      {confirmOrder && (
        <div className="pickup-confirm" role="dialog" aria-modal="true">
          <button className="pickup-confirm__backdrop" type="button" aria-label="Close confirmation" onClick={() => setConfirmOrder(null)} />
          <div className="pickup-confirm__panel">
            <h3>Confirm Collection?</h3>
            <p>Please confirm the customer has collected this order.</p>
            <div className="pickup-confirm__summary">
              <span>Order</span>
              <strong>{confirmOrder.order_number}</strong>
              <span>Customer</span>
              <strong>{confirmOrder.customer_name} / {confirmOrder.customer_phone}</strong>
              <span>Quantity</span>
              <strong>{confirmOrder.quantity}</strong>
              <span>Product</span>
              <strong>{confirmOrder.product_name}</strong>
            </div>
            <div className="pickup-confirm__actions">
              <button type="button" disabled={submitting} onClick={() => setConfirmOrder(null)}>Cancel</button>
              <button type="button" disabled={submitting} onClick={handleConfirmCollected}>
                {submitting ? "Updating..." : "Confirm Collected"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModulePageShell>
  );
};

export default PickupQueuePage;
